import IconButton from 'components/@extended/IconButton';
import { ArrowBack as ArrowBackIcon } from 'components/icons';
import { ListHeaderSlotsContext } from 'components/tables/list-table/filter-bar/ListHeaderSlots';
import React, { useContext, useMemo } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import PageTitle from './PageTitle';

interface Props {
    titleId?: string;
    title?: string;
    titleNode?: React.ReactNode;
    icons?: React.ReactNode;
    iconsEnd?: React.ReactNode;
    buttons?: React.ReactNode;
    showBreadcrumb?: boolean;
    onBack?: () => void;
    children?: React.ReactNode;
    /**
     * In the new design, the title row takes the page's list's count beside the title and its Export before
     * `buttons`; for list pages.
     */
    hostsList?: boolean;
}

/**
 * @param titleId - Translation ID of the page title
 */
const PageHeader: React.FC<Props> = (props) => {
    const {
        children,
        icons,
        iconsEnd,
        buttons,
        titleId,
        title,
        titleNode,
        showBreadcrumb = false,
        onBack,
        hostsList = false,
    } = props;
    // Provided by the new design's layout only, so classic never hosts a list's count or Export here.
    const listSlots = useContext(ListHeaderSlotsContext);
    const setCount = hostsList ? listSlots?.setCount : undefined;
    const setActions = hostsList ? listSlots?.setActions : undefined;

    const pageTitle = useMemo(() => (titleId ? t(titleId) : title), [titleId, title]);

    // The new design's title is one step at every width. Classic's shrinks on a narrow screen, with a line height
    // that keeps the row as tall as the `h2` step's.
    const titleClass =
        'text-h2 new:text-page-title max-sm:not-new:text-[24px] max-sm:not-new:leading-[1.5833] m-0 flex cursor-default items-center gap-2.5 text-balance';
    const titleContent = (
        <>
            {/* The new design's top bar holds the back control. */}
            {onBack && (
                <IconButton
                    onClick={onBack}
                    className="text-fg-secondary hover:text-fg new:hidden -mr-1"
                    aria-label={t('back')}
                >
                    <ArrowBackIcon fontSize="medium" />
                </IconButton>
            )}
            {icons ?? null}
            {titleNode ?? pageTitle}
        </>
    );
    // The page's one `<h1>`, focusable by script so a page change can land on it, with no ring: nothing can be
    // done on it. What follows the title (a record's copy button, a status) sits beside the heading rather than
    // in it, so the heading reads as the title alone; the wrapper then carries the title's type, which both
    // inherit, so the row draws the same with or without `iconsEnd`.
    const heading = iconsEnd ? (
        <div className={titleClass}>
            <h1 tabIndex={-1} className="m-0 flex items-center gap-2.5 outline-none">
                {titleContent}
            </h1>
            {iconsEnd}
        </div>
    ) : (
        <h1 tabIndex={-1} className={cn(titleClass, 'outline-none')}>
            {titleContent}
        </h1>
    );
    // The count reads as part of the title: on its baseline.
    const titleRow = setCount ? (
        <div className="flex items-baseline gap-2.5 self-center">
            {heading}
            <span
                ref={setCount}
                data-list-count
                className="text-muted-foreground text-body1 tabular-nums empty:hidden"
            />
        </div>
    ) : (
        heading
    );

    const header = (
        <div className="text-primary new:text-foreground mt-0.5">
            <div className="flex flex-wrap justify-between gap-4">
                {titleRow}
                <div className="ml-auto flex items-center">
                    {setActions ? (
                        <div className="flex items-center gap-3">
                            <span ref={setActions} className="contents" />
                            {buttons ?? null}
                        </div>
                    ) : (
                        (buttons ?? null)
                    )}
                </div>
            </div>

            <div>{children}</div>
        </div>
    );

    return (
        <>
            <PageTitle title={pageTitle} showBreadcrumb={showBreadcrumb} breadcrumbContent={pageTitle} />
            {header}
        </>
    );
};

export default PageHeader;
